import Twemoji from "react-twemoji";
import { ReactNode } from "react";
import { motion } from "framer-motion";
import { ScopeType } from "sockapi/scope-types";
import { MAX_ANSWER_LENGTH } from "sockapi/scope-specs/team-answerable-spec";
import { SubmitAnswerResult, SubmitAnswerResultType } from "sockapi/answers";
import { toastr } from "Globals";
import { useEffect, useRef, useState } from "react";
import { useUILock } from "client-util/useUILock";
import {
  useTeamAnswerableScopeState,
  useIsHuntOver,
} from "stores/ScopeStateStore";
import {
  useServerInteractionStore,
  useTeamId,
} from "stores/ServerInteractionStore";
import { usePuzName } from "stores/NavStore";
import { Spinner } from "components/Loading";
import { Paper, Metal } from "components/Material";
import { SolveAttemptMessage } from "components/PuzzlePage/PreviousGuessesDisplay";
import settings from "settings";

import "./SubmitAnswerForm.css";

function SubmitAnswerForm() {
  const sockClient = useServerInteractionStore(
    (state) => state.getSockClient
  )();
  const teamId = useTeamId();
  const puzName = usePuzName();
  const submitAnswerLock = useUILock();
  const { cancel: cancelSubmitAnswerLock, isHeld: isSubmitAnswerLockHeld } =
    submitAnswerLock;
  const [submitAnswerResp, setSubmitAnswerResp] =
    useState<SubmitAnswerResult | null>(null);
  const teamAnswerableScopeState = useTeamAnswerableScopeState(teamId, puzName);
  const isHuntOver = useIsHuntOver();
  const answerInputRef = useRef<HTMLInputElement>(null);

  // Reset the answer submission when switching between puzzles.
  useEffect(() => {
    setSubmitAnswerResp(null);
    cancelSubmitAnswerLock();
    if (answerInputRef.current !== null) {
      answerInputRef.current.value = "";
      answerInputRef.current.focus();
    }
  }, [puzName, setSubmitAnswerResp, cancelSubmitAnswerLock, answerInputRef]);

  // Reset the submission box when a submit answer response comes back,
  // independent of the resetting when switching puzzles.
  useEffect(() => {
    if (answerInputRef.current === null) return;
    if (isSubmitAnswerLockHeld) return;
    if (answerInputRef.current !== null) {
      answerInputRef.current.value = "";
      answerInputRef.current.focus();
    }
  }, [answerInputRef, isSubmitAnswerLockHeld]);

  useEffect(() => {
    const handleClick = (ev: MouseEvent) => {
      const evTarget = ev.target as Element;
      if (!evTarget.closest(".submit-answer-form")) setSubmitAnswerResp(null);
    };
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, []);

  const handleSubmitAnswerAck = (
    resp: SubmitAnswerResult,
    isCanceled: boolean
  ) => {
    // If the user switched to a different puzzle while an answer
    // submission was in flight, don't update the DOM, but still
    // show the notification.
    if (!isCanceled) setSubmitAnswerResp(resp);
    switch (resp.type) {
      case SubmitAnswerResultType.ALREADY_SOLVED: {
        toastr.error("You’ve already solved this puzzle!");
        break;
      }
      case SubmitAnswerResultType.NO_GUESSES: {
        toastr.error("You have no more guesses for this puzzle!");
        break;
      }
      case SubmitAnswerResultType.SUCCESS: {
        const { info } = resp;
        // Don't show a toastr if it's correct since we're getting
        // notifications for puzzle solves.
        if (info.isWrong) toastr.error(`${info.answer} is incorrect.`);
        break;
      }
    }
  };

  if (
    puzName === null ||
    teamId === null ||
    teamAnswerableScopeState === null ||
    isHuntOver === null
  )
    return (
      <div className="submit-answer-form">
        <Spinner inline={true} />
      </div>
    );

  const { numGuessesRemaining } = teamAnswerableScopeState;

  const formErrMsgs = ((): readonly ReactNode[] => {
    if (submitAnswerResp === null) return [];
    switch (submitAnswerResp.type) {
      case SubmitAnswerResultType.SUCCESS: {
        const { info } = submitAnswerResp;
        // The answer submission box goes away when the answer is correct,
        // so unfortunately we can't show any messages here anyway.
        // Probably there is a way to make the message show in the UI
        // still, for future hunts.
        if (info.isCorrect ?? false) return [];
        return info.message === undefined
          ? []
          : [
              <SolveAttemptMessage
                key="solve-message"
                teamId={teamId}
                info={info}
              />,
            ];
      }
      case SubmitAnswerResultType.NO_ANSWER: {
        return [
          "The answer should have at least one letter A through Z (case does not matter).",
        ];
      }
      case SubmitAnswerResultType.NOT_NUMERICAL: {
        return ["The answer should be a number."];
      }
      case SubmitAnswerResultType.TRIED_BEFORE: {
        return [
          `You’ve already tried submitting the answer “${submitAnswerResp.submittedAnswer}” for this puzzle.`,
        ];
      }
      default:
        return [];
    }
  })();

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitAnswerLockHeld) return;
    if (answerInputRef.current === null) return;
    if (sockClient === null) return;
    if (teamId === null) return;

    const answer = answerInputRef.current.value;
    if (answer.trim().length <= 0) return;

    submitAnswerLock.acquireAndRun(async (getIsCanceled) => {
      const payload = await sockClient.sendScopeStepAndWaitAsync(
        {
          type: ScopeType.TEAM_ANSWERABLE,
          teamId,
          puzName,
        },
        {
          submitAnswer: answer,
        }
      );
      if (payload === null) return;
      handleSubmitAnswerAck(payload, getIsCanceled());
    });
  };

  const remainingGuessesText = ((): string => {
    if (settings.isPosthunt) return "Submit answer";
    if (numGuessesRemaining <= 0)
      return "You have no more guesses remaining for this puzzle!";
    else if (numGuessesRemaining >= 2)
      return `You have ${numGuessesRemaining} guesses remaining for this puzzle.`;
    else return "You have 1 guess remaining for this puzzle.";
  })();

  return (
    <motion.form
      className="submit-answer-form"
      key="submit-answer-form"
      onSubmit={submit}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ transform: "translateY(100%)" }}
      transition={{ duration: 0.15 }}
    >
      <div className="input-group">
        <Paper seed="SubmitAnswerForm: answer box">
          {formErrMsgs.map((msg, i) => (
            <div key={i} className="submit-answer-form-message">
              {msg}
            </div>
          ))}
          <input
            type="text"
            name="answer"
            autoFocus={true}
            autoComplete="off"
            maxLength={MAX_ANSWER_LENGTH}
            required={true}
            disabled={
              isSubmitAnswerLockHeld ||
              (!isHuntOver && numGuessesRemaining <= 0)
            }
            ref={answerInputRef}
            placeholder={remainingGuessesText}
          />
        </Paper>
        <a
          className="submit"
          href="#"
          onClick={(evt) => {
            (evt.target as Element).closest("form")?.requestSubmit();
          }}
          title="Check answer"
        >
          <Metal seed="SubmitAnswerForm: submit button">
            <Twemoji>📨️</Twemoji>
          </Metal>
        </a>
      </div>
    </motion.form>
  );
}

export default SubmitAnswerForm;
